import React, { useState, useEffect } from 'react'
import {
  Form,
  Button,
  Modal,
  Row,
  Col,
  Spin,
  message,
  Upload,
  Tooltip,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined,
  UploadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import FormInput from './FormInput'
import { createBookThunk, updateBookThunk } from '../redux/features/books/booksThunk'
import { createVideoThunk, updateVideoThunk } from '../redux/features/videos/videoThunk'
import { createAudioThunk, updateAudioThunk } from '../redux/features/audio/audioThunk'

const { Text } = Typography

interface EditData {
  id: string
  title: string
  description: string
  category: string
  thumbnail?: string
  url?: string
}

interface UploadFormModalProps {
  visible: boolean
  onClose: () => void
  label: 'video' | 'audio' | 'book'
  editData?: EditData | null
}

const UploadFormModal = ({ visible, onClose, label, editData }: UploadFormModalProps) => {
  const [form] = Form.useForm()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [uploading, setUploading] = useState<boolean>(false)
  const dispatch = useDispatch<any>()

  const isEditMode = !!editData

  // Populate form with edit data
  useEffect(() => {
    if (editData && visible) {
      setTitle(editData.title || '')
      setDescription(editData.description || '')
      setCategory(editData.category || '')
      form.setFieldsValue({
        title: editData.title,
        description: editData.description,
        category: editData.category,
      })
    } else if (!visible) {
      // Reset form when modal closes
      setTitle('')
      setDescription('')
      setCategory('')
      form.resetFields()
    }
  }, [editData, visible, form])

  const aspectRatios: any = {
    video: '40/100 - aspect ratio',
    book: 'Thumbnail aspect ratio: 15 / 21',
    audio: 'Thumbnail aspect ratio: 16 / 9',
    videoThumbnail: 'Thumbnail aspect ratio: 16 / 9',
  }

  const onFinish = async (values: any) => {
    setUploading(true)
    console.log('Received values of form: ', values)

    // For create mode, require both file and thumbnail
    if (!isEditMode) {
      if (
        !values.file ||
        values.file.length === 0 ||
        !values.file[0].originFileObj
      ) {
        message.error('Please upload the file.')
        setUploading(false)
        return
      }

      if (
        !values.thumbnail ||
        values.thumbnail.length === 0 ||
        !values.thumbnail[0].originFileObj
      ) {
        message.error('Please upload the thumbnail.')
        setUploading(false)
        return
      }
    }

    try {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('description', description)
      formData.append('category', category)
      formData.append('label', label)

      // Add file only if a new one was uploaded
      if (values.file && values.file.length > 0 && values.file[0].originFileObj) {
        formData.append('file', values.file[0].originFileObj)
      }

      // Add thumbnail only if a new one was uploaded
      if (values.thumbnail && values.thumbnail.length > 0 && values.thumbnail[0].originFileObj) {
        formData.append('thumbnail', values.thumbnail[0].originFileObj)
      }

      if (isEditMode && editData) {
        // Update mode
        switch (label) {
          case 'audio':
            await dispatch(updateAudioThunk({ id: editData.id, audioData: formData })).unwrap()
            break
          case 'book':
            await dispatch(updateBookThunk({ bookId: editData.id, bookData: formData })).unwrap()
            break
          case 'video':
            await dispatch(updateVideoThunk({ videoId: editData.id, videoData: formData })).unwrap()
            break
          default:
            message.error(`Unsupported file type ${label}`)
            setUploading(false)
            return
        }

        Modal.success({
          title: 'Update Completed',
          content: (
            <div>
              <CheckCircleOutlined style={{ color: 'green', fontSize: '20px' }} />
              <p>Your content has been successfully updated.</p>
            </div>
          ),
        })
      } else {
        // Create mode
        switch (label) {
          case 'audio':
            await dispatch(createAudioThunk(formData)).unwrap()
            break
          case 'book':
            await dispatch(createBookThunk(formData)).unwrap()
            break
          case 'video':
            await dispatch(createVideoThunk(formData)).unwrap()
            break
          default:
            message.error(`Unsupported file type ${label}`)
            setUploading(false)
            return
        }

        Modal.success({
          title: 'Upload Completed',
          content: (
            <div>
              <CheckCircleOutlined style={{ color: 'green', fontSize: '20px' }} />
              <p>Your file has been successfully uploaded.</p>
            </div>
          ),
        })
      }

      onClose()
    } catch (error) {
      message.error(isEditMode ? 'Update failed' : 'Upload failed')
      console.error('Operation error:', error)
    } finally {
      setUploading(false)
    }
  }

  const getGuidelinesText = () => {
    switch (label) {
      case 'audio':
        return <>- Audio Thumbnail: should have aspect ratio: 16 / 9</>
      case 'book':
        return <>- Book Thumbnail: should have aspect ratio: 15 / 21</>
      case 'video':
        return (
          <>
            - Video: Your video should have 40/100 - aspect ratio <br />- Video
            Thumbnail: should have aspect ratio: 16 / 9
          </>
        )
      default:
        return null
    }
  }

  return (
    <Modal
      title={isEditMode ? `Edit ${label}` : `Upload new ${label}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="uploadForm"
          htmlType="submit"
          loading={uploading}
        >
          {isEditMode ? 'Update' : 'Upload'}
        </Button>,
      ]}
      forceRender
    >
      <Spin spinning={uploading} tip="Uploading...">
        <Form form={form} layout="vertical" onFinish={onFinish} id="uploadForm">
          <Row gutter={16}>
            <Col span={12}>
              <FormInput
                name="title"
                label="Title"
                type="text"
                value={title}
                onChange={setTitle}
                rules={[{ required: true }]}
              />
            </Col>
            <Col span={12}>
              <FormInput
                name="description"
                label="Description"
                type="textarea"
                value={description}
                onChange={setDescription}
                rules={[{ required: true }]}
              />
            </Col>
            <Col span={12}>
              <FormInput
                name="category"
                label="Category"
                type="text"
                value={category}
                onChange={setCategory}
                rules={[{ required: true }]}
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name="thumbnail"
                label={
                  <span>
                    {isEditMode ? 'Update Thumbnail (optional)' : 'Upload Thumbnail'}{' '}
                    <Tooltip
                      title={`Aspect ratio guidelines: ${aspectRatios[label] || 'N/A'
                        }`}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
                valuePropName="fileList"
                getValueFromEvent={(e) => e && e.fileList}
                rules={[
                  { required: !isEditMode, message: 'Please upload a thumbnail!' },
                ]}
              >
                <Upload
                  name="thumbnail"
                  listType="picture"
                  beforeUpload={() => false}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="file"
                label={
                  label === 'video' ? (
                    <span>
                      {isEditMode ? 'Update File (optional)' : 'Upload File'}{' '}
                      <Tooltip
                        title={`Video aspect ratio: ${aspectRatios.video}`}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  ) : (
                    isEditMode ? 'Update File (optional)' : 'Upload File'
                  )
                }
                valuePropName="fileList"
                getValueFromEvent={(e) => e && e.fileList}
                rules={[{ required: !isEditMode, message: 'Please upload a file!' }]}
              >
                <Upload
                  name="file"
                  beforeUpload={() => false}
                  accept=".pdf,.mp4,.mp3"
                >
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Text type="secondary">
                Guidelines: <br />
                {getGuidelinesText()}
              </Text>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  )
}

export default UploadFormModal
